import { useNavigate, useParams } from "@tanstack/react-router";
import { motion } from "motion/react";
import type { JSX } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import type { Template } from "@/common/types/templates";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { TemplateForm } from "@/components/ui/templates/template-form";
import { useTemplateById } from "@/hooks/templates/useTemplateById";
import { useTemplateWrites } from "@/hooks/templates/useTemplateWrites";
import { toast } from "@/hooks/use-toast";
import { refreshTemplateReads } from "@/lib/template-queries";
import { container } from "@/lib/utils";
import { getVisitScope, type VisitScope } from "@/lib/visit-queries";
import type { TemplateFormSchemaType } from "@/schemas/template.schema";
import { TemplateDocumentEditor } from "../Templates/redesign/TemplateDocumentEditor";
import { TemplateEditorShell } from "../Templates/redesign/TemplateEditorShell";

export const TemplateEdit = (): JSX.Element => {
  const { t } = useTranslation();
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const {
    templatePublicId: routeTemplatePublicId,
  }: { templatePublicId: string } = useParams({
    strict: false,
  });
  const templatePublicId = routeTemplatePublicId.toLowerCase();
  const { update, loading } = useTemplateWrites();
  const {
    result: template,
    loading: templateLoading,
    error: templateError,
    refresh,
  } = useTemplateById(templatePublicId);
  const navigate = useNavigate();
  const handleAssistantSaved = (
    _template: Template,
    scope: VisitScope | null,
  ): void => {
    if (!scope || getVisitScope() !== scope) return;
    if (!mounted.current) {
      void refreshTemplateReads(scope);
      return;
    }
    toast({
      variant: "default",
      title: t("templates.updatedToast.successful.title"),
      description: t("templates.updatedToast.successful.description"),
    });
    void refreshTemplateReads(scope);
    void navigate({ to: "/templates" });
  };
  // v3.2 editor: whether the last save went through (it then leaves).
  const savedRef = useRef(false);
  const handleSuccess = async (data: TemplateFormSchemaType): Promise<void> => {
    savedRef.current = false;
    const outcome = await update(templatePublicId, {
      name: data.name,
      headers: data.headers,
      optionalText: data.optionalText ?? "",
      isDefault: data.isDefault,
    });
    if (!outcome || getVisitScope() !== outcome.scope) return;
    if (!mounted.current) {
      if (outcome.ok) void refreshTemplateReads(outcome.scope);
      return;
    }
    toast({
      variant: outcome.ok ? "default" : "error",
      title: t(
        outcome.ok
          ? "templates.updatedToast.successful.title"
          : "templates.updatedToast.error.title",
      ),
      description: t(
        outcome.ok
          ? "templates.updatedToast.successful.description"
          : "templates.updatedToast.error.description",
      ),
    });
    if (outcome.ok) {
      savedRef.current = true;
      void refreshTemplateReads(outcome.scope);
      void navigate({ to: "/templates" });
    }
  };

  const form = template && (
    <TemplateForm
      isSubmitting={loading}
      submitText={t("templates.editTemplate.submit")}
      template={template}
      assistant={{
        templatePublicId: templatePublicId,
        onSaved: handleAssistantSaved,
      }}
      onCancel={() => navigate({ to: "/templates" })}
      onSubmit={handleSuccess}
    />
  );
  // v3.2: the "Document" editor (section cards, Ask bar), full column.
  if (AppConfig.enableAppRedesign && template && !templateError)
    return (
      <Layout navbarTitle={t("templates.title")} topBar="none">
        <div className="absolute inset-0">
          <TemplateDocumentEditor
            key={template.public_id}
            saving={loading}
            template={template}
            templatePublicId={templatePublicId}
            onClose={() => {
              void navigate({ to: "/templates" });
            }}
            onSave={async (write) => {
              await handleSuccess(write);
              return savedRef.current;
            }}
          />
        </div>
      </Layout>
    );
  if (AppConfig.enableAppRedesign)
    return (
      <Layout
        navbarTitle={t("templates.title")}
        breadcrumbs={[
          { name: t("templates.title"), href: `/templates`, current: true },
        ]}
      >
        <TemplateEditorShell
          error={!!templateError}
          loading={templateLoading}
          mode="edit"
          title={template?.name}
          onRetry={() => {
            void refresh();
          }}
        >
          {form}
        </TemplateEditorShell>
      </Layout>
    );

  return (
    <Layout
      navbarTitle={t("templates.title")}
      breadcrumbs={[
        { name: t("templates.title"), href: `/templates`, current: true },
      ]}
    >
      <div className="min-h-screen supports-[min-height:100dvh]:min-h-dvh bg-background p-6">
        <motion.div
          animate="show"
          className="mx-auto max-w-5xl space-y-8"
          initial="hidden"
          variants={container}
        >
          {templateLoading && <LoadingSpinner size={8} />}
          {templateError && (
            <div role="alert">
              <p>{t("error.unknown")}</p>
              <Button
                onClick={() => {
                  void refresh();
                }}
              >
                {t("common.retry", "Retry")}
              </Button>
            </div>
          )}
          {template && form}
        </motion.div>
      </div>
    </Layout>
  );
};
